
import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Tag, Package, Database } from 'lucide-react';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { db, storage } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import ImageUploader from './ImageUploader';
import SmartImage from '../SmartImage';

const CategoriesManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryImage, setCategoryImage] = useState('');

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.CATEGORIES));
      const cats = [];
      querySnapshot.forEach((doc) => {
        cats.push({ id: doc.id, ...doc.data() });
      });
      setCategories(cats);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const categoryData = {
      name: formData.get('name'),
      nameEn: formData.get('nameEn'),
      description: formData.get('description'),
      icon: formData.get('icon') || '📦',
      color: formData.get('color') || 'blue',
      active: true,
      createdAt: Timestamp.now()
    };

    try {
      if (editingCategory) {
        // التحقق من وجود التصنيف قبل التحديث
        try {
          await updateDoc(doc(db, COLLECTIONS.CATEGORIES, editingCategory.id), categoryData);
        } catch (updateError) {
          if (updateError.code === 'not-found') {
            alert('هذا التصنيف غير موجود في قاعدة البيانات. سيتم إضافته كتصنيف جديد.');
            await addDoc(collection(db, COLLECTIONS.CATEGORIES), categoryData);
          } else {
            throw updateError;
          }
        }
      } else {
        await addDoc(collection(db, COLLECTIONS.CATEGORIES), categoryData);
      }
      setShowModal(false);
      setEditingCategory(null);
      loadCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      alert(`حدث خطأ أثناء حفظ التصنيف: ${error.message}`);
    }
  };

  const handleDelete = async (categoryId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا التصنيف؟')) {
      try {
        await deleteDoc(doc(db, COLLECTIONS.CATEGORIES, categoryId));
        // حذف محلي لتحديث الواجهة فوراً
        setCategories(prev => prev.filter(c => c.id !== categoryId));
      } catch (error) {
        console.error('Error deleting category:', error);
        if (error.code === 'not-found') {
          // إذا لم يكن موجوداً في Firebase، نزيله من الحالة المحلية فقط
          setCategories(prev => prev.filter(c => c.id !== categoryId));
          alert('تم حذف التصنيف من الواجهة (لم يكن موجوداً في قاعدة البيانات).');
        } else {
          alert(`حدث خطأ أثناء حذف التصنيف: ${error.message}`);
        }
      }
    }
  };

  // حذف التصنيفات الغريبة (التركية وغير العربية/الإنجليزية)
  const deleteWeirdCategories = async () => {
    const weirdKeywords = [
      'tatlı', 'ekmek', 'pide', 'döner', 'doner', 'pizza', 'çiğ', 'köfte',
      'ev yemekleri', 'yemek', 'tatli', 'cigköfte', 'cig', 'kofte'
    ];

    if (!window.confirm('هل تريد حذف جميع التصنيفات الغريبة (التركية وغيرها)؟\n\nسيتم حذف: Tatlı Ekmek, Pizza, Çiğ köfte, Döner, Pide, Ev yemekleri وغيرها')) {
      return;
    }

    setLoading(true);
    let deletedCount = 0;
    const deletedNames = [];

    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.CATEGORIES));

      for (const docSnap of querySnapshot.docs) {
        const category = docSnap.data();
        const categoryName = (category.name || '').toLowerCase();
        const categoryNameEn = (category.nameEn || '').toLowerCase();

        // التحقق إذا كان اسم التصنيف يحتوي على أي من الكلمات المفتاحية الغريبة
        const isWeird = weirdKeywords.some(keyword =>
          categoryName.includes(keyword) ||
          categoryNameEn.includes(keyword)
        );

        // أو التحقق من الأحرف التركية الخاصة
        const hasTurkishChars = /[çğıöşü]/i.test(categoryName) || /[çğıöşü]/i.test(categoryNameEn);

        if (isWeird || hasTurkishChars) {
          await deleteDoc(doc(db, 'categories', docSnap.id));
          deletedCount++;
          deletedNames.push(category.name || category.nameEn || 'غير معروف');
          console.log(`✅ Deleted: ${category.name} `);
        }
      }

      if (deletedCount > 0) {
        console.log('🗑️ Deleted categories:', deletedNames);
        alert(`✅ تم حذف ${deletedCount} تصنيف بنجاح!\n\nالتصنيفات المحذوفة: \n${deletedNames.join(', ')} `);
      } else {
        alert('⚠️ لم يتم العثور على تصنيفات غريبة للحذف');
      }

      loadCategories();
    } catch (error) {
      console.error('Error deleting weird categories:', error);
      alert(`❌ حدث خطأ أثناء الحذف: ${error.message} `);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  const filteredCategories = categories.filter(cat =>
    cat.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const colorOptions = [
    { name: 'أزرق', value: 'blue', bg: 'bg-blue-500' },
    { name: 'أخضر', value: 'green', bg: 'bg-green-500' },
    { name: 'أصفر', value: 'yellow', bg: 'bg-yellow-500' },
    { name: 'أحمر', value: 'red', bg: 'bg-red-500' },
    { name: 'بنفسجي', value: 'purple', bg: 'bg-purple-500' },
    { name: 'برتقالي', value: 'orange', bg: 'bg-orange-500' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">إدارة التصنيفات</h1>
          <p className="text-gray-600 mt-2">إجمالي التصنيفات: {categories.length}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={deleteWeirdCategories}
            disabled={loading}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-3 rounded-lg
                     flex items-center gap-2 font-medium transition-colors disabled:opacity-50"
            title="حذف التصنيفات الغريبة (مثل: Pide, Döner, Tatlı)"
          >
            <Trash2 className="w-5 h-5" />
            حذف التصنيفات الغريبة
          </button>
          <button
            onClick={() => {
              setEditingCategory(null);
              setShowModal(true);
            }}
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-lg
                     flex items-center gap-2 font-medium transition-colors"
          >
            <Plus className="w-5 h-5" />
            إضافة تصنيف جديد
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="relative">
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="ابحث عن تصنيف..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg
                     focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
          />
        </div>
      </div>

      {filteredCategories.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCategories.map((category) => (
            <div key={category.id} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden">
              <div className={`h - 2 bg - ${category.color} -500`}></div>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-16 flex items-center justify-center">
                      <SmartImage
                        src={category.icon}
                        alt={category.name}
                        size="medium"
                        fallback="📦"
                      />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{String(category.name || '')}</h3>
                      <p className="text-sm text-gray-500">{String(category.nameEn || '')}</p>
                    </div>
                  </div>
                  <div className={`w - 3 h - 3 rounded - full ${category.active ? 'bg-green-500' : 'bg-gray-300'} `}></div>
                </div>

                {category.description && (
                  <p className="text-sm text-gray-600 mb-4">{String(category.description)}</p>
                )}

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleEdit(category)}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg
                             flex items-center justify-center gap-2 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    تعديل
                  </button>
                  <button
                    onClick={() => handleDelete(category.id)}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg
                             flex items-center justify-center gap-2 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    حذف
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md p-12 text-center text-gray-500">
          <Tag className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p>لا توجد تصنيفات متاحة</p>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {editingCategory ? 'تعديل التصنيف' : 'إضافة تصنيف جديد'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                  اسم التصنيف بالعربي
                </label>
                <input
                  type="text"
                  name="name"
                  defaultValue={editingCategory?.name}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                  اسم التصنيف بالإنجليزي
                </label>
                <input
                  type="text"
                  name="nameEn"
                  defaultValue={editingCategory?.nameEn}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <ImageUploader
                currentImage={editingCategory?.icon || categoryImage}
                onImageChange={setCategoryImage}
                label="صورة أو رمز التصنيف"
              />
              <input type="hidden" name="icon" value={categoryImage} />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                  اللون
                </label>
                <select
                  name="color"
                  defaultValue={editingCategory?.color || 'blue'}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
                >
                  {colorOptions.map((color) => (
                    <option key={color.value} value={color.value}>
                      {color.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                  الوصف
                </label>
                <textarea
                  name="description"
                  defaultValue={editingCategory?.description}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white py-3 rounded-lg
                           font-medium transition-colors"
                >
                  {editingCategory ? 'حفظ التعديلات' : 'إضافة التصنيف'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCategory(null);
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-3 rounded-lg
                           font-medium transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoriesManagement;
